import { useCallback, useRef, useState } from 'react';
import config from '../config';
import { Error as ApiError, SearchResults } from '../types';
import prepareHeaders from '../utilities/prepare-headers';
import { useAuth } from './auth';

const useSearch = () => {
  const { token } = useAuth();

  const [pending, setPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Searches can overlap when searching as the user types, so only the most
  // recent request is allowed to update the pending and error states
  const latestRequest = useRef(0);

  const resetError = useCallback(() => {
    setError(null);
  }, []);

  const search = useCallback(
    async (
      query: string,
      signal?: AbortSignal
    ): Promise<SearchResults | void> => {
      const request = ++latestRequest.current;
      const isLatest = () => request === latestRequest.current;

      setPending(true);

      const url = `${config.api.url}/dashboard/search?query=${encodeURIComponent(
        query
      )}`;

      return fetch(url, {
        method: 'GET',
        headers: prepareHeaders(token),
        signal,
      })
        .then(async response => {
          if (!isLatest()) {
            return;
          }

          setPending(false);
          setError(null);

          if (!response.ok) {
            setError(new Error(((await response.json()) as ApiError).message));
            return;
          }

          return (await response.json()) as SearchResults;
        })
        .catch((error: Error) => {
          if (!isLatest()) {
            return;
          }

          setPending(false);

          if (error.name !== 'AbortError') {
            setError(error);
          }
        });
    },
    [token]
  );

  return {
    pending,
    error,
    resetError,
    search,
  };
};

export default useSearch;
